import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axiosInstance from '@/lib/axios'
import type { PurchaseRequest, PurchaseRequestCreate, PurchaseRequestUpdate } from '@/types'

const API_BASE = '/purchase-requests'

/**
 * Hook per gestire le operazioni CRUD delle RDA
 */
export const useRdas = () => {
  const queryClient = useQueryClient()

  // Get RDAs for a specific intervention
  const { data: rdas, isLoading: rdaLoading, refetch: refetchRdas } = useQuery({
    queryKey: ['rdas'],
    queryFn: async () => {
      const response = await axiosInstance.get<PurchaseRequest[]>(`${API_BASE}/`)
      return response.data
    },
  })

  // Get RDAs for a specific intervention
  const getRdasForIntervention = async (interventionId: number) => {
    try {
      const response = await axiosInstance.get<PurchaseRequest[]>(
        `/work-orders/intervention/${interventionId}/purchase-requests`
      )
      return response.data
    } catch (error: any) {
      console.error('❌ Error loading RDAs:', error.message)
      throw error
    }
  }

  // Get single RDA detail
  const getRdaDetail = async (rdaId: number) => {
    try {
      const response = await axiosInstance.get<PurchaseRequest>(`${API_BASE}/${rdaId}`)
      return response.data
    } catch (error: any) {
      console.error('❌ Error loading RDA detail:', error.message)
      throw error
    }
  }

  // Create RDA
  const createMutation = useMutation({
    mutationFn: async (data: PurchaseRequestCreate) => {
      const response = await axiosInstance.post<PurchaseRequest>(`${API_BASE}/`, data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rdas'] })
      queryClient.invalidateQueries({ queryKey: ['workOrders'] })
    },
  })

  // Update RDA
  const updateMutation = useMutation({
    mutationFn: async ({ rdaId, data }: { rdaId: number; data: PurchaseRequestUpdate }) => {
      const url = `${API_BASE}/${rdaId}`
      
      const response = await axiosInstance.patch<PurchaseRequest>(url, data)
      
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rdas'] })
      queryClient.invalidateQueries({ queryKey: ['workOrders'] })
    },
    onError: (error: any) => {
      console.error('❌ MUTATION ERROR:', error)
    }
  })

  // Delete RDA
  const deleteMutation = useMutation({
    mutationFn: async (rdaId: number) => {
      await axiosInstance.delete(`${API_BASE}/${rdaId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rdas'] })
      queryClient.invalidateQueries({ queryKey: ['workOrders'] })
    },
    onError: () => {
      // Invalida anche in caso di errore per ricaricare i dati freschi
      queryClient.invalidateQueries({ queryKey: ['rdas'] })
      queryClient.invalidateQueries({ queryKey: ['workOrders'] })
    },
  })

  return {
    rdas,
    rdaLoading,
    refetchRdas,
    getRdasForIntervention,
    getRdaDetail,
    create: createMutation.mutate,
    createAsync: createMutation.mutateAsync,
    update: updateMutation.mutate,
    updateAsync: updateMutation.mutateAsync,
    delete: deleteMutation.mutate,
    deleteAsync: deleteMutation.mutateAsync,
  }
}
